import React from "react";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { IParticipationSummary } from "@Interfaces";
import {
    getParticipationDataPhase,
    PARTICIPATION_BODY_TEXT,
    PARTICIPATION_FONT_FAMILY,
    PARTICIPATION_INDIGO,
    PARTICIPATION_INDIGO_TINT,
    PARTICIPATION_INSET_BG,
    PARTICIPATION_MUTED_TEXT,
    PARTICIPATION_ROW_HEIGHT,
    PARTICIPATION_TEXT_MD,
    PARTICIPATION_TEXT_SM,
    PARTICIPATION_TEXT_XS,
    PARTICIPATION_TITLE_SIZE,
    PARTICIPATION_WARNING_BG,
    PARTICIPATION_WARNING_TEXT,
} from "./participationStyles";

interface HowFarPeopleGotProps {
    summary: IParticipationSummary;
}

const mutedPillSx = {
    fontSize: PARTICIPATION_TEXT_XS,
    fontWeight: 500,
    color: PARTICIPATION_MUTED_TEXT,
    backgroundColor: "#EEF0F5",
    padding: "4px 10px",
    borderRadius: "999px",
};

const indigoPillSx = {
    fontSize: PARTICIPATION_TEXT_XS,
    fontWeight: 700,
    color: PARTICIPATION_INDIGO,
    backgroundColor: PARTICIPATION_INDIGO_TINT,
    padding: "4px 10px",
    borderRadius: "999px",
};

const warningPillSx = {
    fontSize: PARTICIPATION_TEXT_XS,
    fontWeight: 700,
    letterSpacing: "0.04em",
    color: PARTICIPATION_WARNING_TEXT,
    backgroundColor: PARTICIPATION_WARNING_BG,
    padding: "4px 10px",
    borderRadius: "999px",
};

export const HowFarPeopleGot: React.FC<HowFarPeopleGotProps> = ({
    summary,
}) => {
    const { t } = useTranslation();
    const phase = getParticipationDataPhase(summary.respondents);

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E8EBF0",
                borderRadius: "12px",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
                fontFamily: PARTICIPATION_FONT_FAMILY,
                color: PARTICIPATION_BODY_TEXT,
            }}
        >
            <Box sx={{ fontSize: PARTICIPATION_TITLE_SIZE, fontWeight: 700 }}>
                {t("pages.participation.widgets.howFarPeopleGot.title")}
            </Box>
            <Box
                sx={{
                    backgroundColor: PARTICIPATION_INSET_BG,
                    borderRadius: "10px",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}
            >
                {summary.answers_per_question.map((question, index) => {
                    const n = question.answered_count;
                    const prev =
                        index === 0
                            ? null
                            : summary.answers_per_question[index - 1]
                                  .answered_count;

                    let left: string;
                    let right: string;
                    let pill: React.ReactNode = null;
                    let barWidth = 0;

                    if (phase === "none") {
                        left = t(
                            "pages.participation.widgets.howFarPeopleGot.noAnswersYet"
                        );
                        right = "–";
                        if (index === 0) {
                            pill = (
                                <Box component="span" sx={mutedPillSx}>
                                    {t(
                                        "pages.participation.widgets.howFarPeopleGot.waitingForAnswers"
                                    )}
                                </Box>
                            );
                        }
                    } else if (phase === "few") {
                        left = t(
                            "pages.participation.widgets.howFarPeopleGot.fewLeft",
                            { n }
                        );
                        right = "";
                        barWidth = (n / summary.respondents) * 100;
                        if (index === 0) {
                            pill = (
                                <Box component="span" sx={mutedPillSx}>
                                    {t(
                                        "pages.participation.widgets.howFarPeopleGot.tooFewToCompare"
                                    )}
                                </Box>
                            );
                        }
                    } else {
                        left = t(
                            "pages.participation.widgets.howFarPeopleGot.manyLeft",
                            { n }
                        );
                        right = `${Math.round(
                            (n / summary.respondents) * 100
                        )}%`;
                        barWidth = (n / summary.respondents) * 100;

                        if (prev === null) {
                            pill = (
                                <Box component="span" sx={indigoPillSx}>
                                    {t(
                                        "pages.participation.widgets.howFarPeopleGot.everyoneStartsHere"
                                    )}
                                </Box>
                            );
                        } else if (n < prev) {
                            // Later questions can have more answers (skipped or
                            // out-of-order answers); only a real drop gets a pill.
                            const drop = (prev - n) / prev;
                            const big = drop >= 0.5;
                            pill = (
                                <Box
                                    component="span"
                                    sx={big ? warningPillSx : indigoPillSx}
                                >
                                    {t(
                                        "pages.participation.widgets.howFarPeopleGot.dropFromPrevious",
                                        {
                                            pct: Math.round(drop * 100),
                                            num: index,
                                        }
                                    )}
                                </Box>
                            );
                        }
                    }

                    return (
                        <Box
                            key={question.question_id}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "10px",
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: PARTICIPATION_TEXT_MD,
                                        fontWeight: 700,
                                    }}
                                >
                                    Q{index + 1} · {question.question_text}
                                </Box>
                                {pill}
                            </Box>
                            <Box
                                sx={{
                                    position: "relative",
                                    height: PARTICIPATION_ROW_HEIGHT,
                                    borderRadius: "8px",
                                    backgroundColor: "#EEF0F8",
                                    overflow: "hidden",
                                    display:
                                        phase === "none" ? "none" : "block",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        backgroundColor: "#E0E3F6",
                                        width: `${barWidth}%`,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        padding: "0 14px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        fontSize: PARTICIPATION_TEXT_SM,
                                    }}
                                >
                                    <Box component="span">{left}</Box>
                                    <Box component="b">{right}</Box>
                                </Box>
                            </Box>
                            {phase === "none" && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        fontSize: PARTICIPATION_TEXT_SM,
                                        color: PARTICIPATION_MUTED_TEXT,
                                    }}
                                >
                                    <Box component="span">{left}</Box>
                                    <Box component="b">{right}</Box>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};
